'use client';
import { OrderLoyaltyReward } from '@eliadmin/core/src/components/loyalty/order-loyalty-reward';

import { useId, type ReactNode } from 'react';

import type { OrderInfo } from '@eliadmin/core/src/api/claims/claims-api';
import type { CustomerOrder } from '@eliadmin/core/src/api/customers/customers-types';
import { OrderInfoCards } from '@eliadmin/core/src/components/nt/account/orders/order-info';
import { OrderItemsCard } from '@eliadmin/core/src/components/nt/account/orders/order-items';
import {
  OrderStatusBadge,
  OrdersMessage,
  ReorderMessage,
} from '@eliadmin/core/src/components/nt/account/orders/order-parts';
import {
  orderStage,
  type OrderStage,
} from '@eliadmin/core/src/components/nt/account/orders/order-stage';
import {
  countLabels,
  statusSteps,
  useOrderTexts,
} from '@eliadmin/core/src/components/nt/account/orders/order-texts';
import { OrderTimeline } from '@eliadmin/core/src/components/nt/account/orders/order-timeline';
import {
  orderCounts,
  orderDate,
  orderNumber,
  orderTime,
  trackingUrl,
  useCustomerOrders,
  useOrderInfo,
} from '@eliadmin/core/src/components/nt/account/orders/orders-data';
import { orderReturnHref } from '@eliadmin/core/src/components/nt/account/shell/account-pages';
import { useOrderDocument } from '@eliadmin/core/src/components/nt/account/orders/use-order-document';
import { useReorder } from '@eliadmin/core/src/components/nt/account/orders/use-reorder';
import { REORDER_LABEL } from '@eliadmin/core/src/components/nt/account/orders/reorder-action';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import Link from '@eliadmin/core/src/components/link';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { GenericLoader } from '@eliadmin/core/src/components/ui/generic-loader';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cleanHref, fillTemplate, stripTags } from '@eliadmin/core/src/utils/nt/text';

/** Figma „Back“: 16 px caret and Body/M Bold #0E6A20, 6 px apart. */
function BackLink() {
  const { account } = useOrderTexts();

  return (
    <Link
      href={ROUTES.ORDERS()}
      prefetch={false}
      className="nt-button-m flex w-fit items-center gap-1.5 rounded text-[#0E6A20] transition-colors hover:text-[#0B5719] focus-visible:shadow-focus focus-visible:outline-none"
    >
      <Icon name="caret-left" size={16} />
      {account.backToOrders}
    </Link>
  );
}

/**
 * Figma „Heading“ (208:12024): H3 number with the status pill, „Vytvorená … · 3 produkty, 4 ks“,
 * „Faktúra (PDF)“ (only with a document) and „Kúpiť znova“. The buttons stay beside the title while
 * both fit, then wrap under it (on phones sharing the width).
 */
function DetailHeader({
  order,
  info,
  stage,
}: {
  order: CustomerOrder;
  info: OrderInfo;
  stage: OrderStage;
}) {
  const { account, checkout } = useOrderTexts();
  const { document, download, loading } = useOrderDocument(info.documents);
  const reorder = useReorder(order);
  const counts = countLabels(checkout, orderCounts(info.items ?? []));
  const meta = counts
    ? fillTemplate(account.createdAt, {
        datum: orderDate(info.created_at),
        cas: orderTime(info.created_at),
        ...counts,
      })
    : '';

  return (
    <div className="flex flex-col gap-3 pb-2">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <h1 className="nt-h3 min-w-0 text-[#0D110D] [overflow-wrap:anywhere]">
              {fillTemplate(account.order, { cislo: orderNumber(order) })}
            </h1>
            <OrderStatusBadge status={info.status} stage={stage} />
          </div>
          {meta ? <p className="nt-body-m text-[#5D655D]">{meta}</p> : null}
        </div>
        {document || reorder.available ? (
          <div className="flex w-full flex-wrap gap-3 sm:w-auto">
            {document ? (
              <Button
                variant="outline"
                size="md"
                iconLeft="download-simple"
                onClick={download}
                isLoading={loading}
                className="flex-1 sm:flex-none"
              >
                {account.invoice}
              </Button>
            ) : null}
            {reorder.available ? (
              <Button
                variant="primary"
                size="md"
                iconLeft="arrow-clockwise"
                onClick={reorder.reorder}
                isLoading={reorder.pending}
                disabled={reorder.disabled}
                className="w-full whitespace-normal sm:w-auto"
              >
                {REORDER_LABEL}
              </Button>
            ) : null}
          </div>
        ) : null}
      </header>
      <ReorderMessage result={reorder.result} />
    </div>
  );
}

/**
 * Figma „Help“ (208:12024): bg #F5F6F3 box with the lifebuoy, „Máte problém s objednávkou?“ and
 * „Vrátiť tovar“ (the return form, opened at this order) / „Reklamovať“ (the claims page). Stacks
 * under 40rem.
 */
function HelpBox({ identifier }: { identifier: string }) {
  const { account } = useOrderTexts();
  const complainHref = cleanHref(stripTags(account.complainHref));
  const titleId = useId();

  return (
    <section
      aria-labelledby={titleId}
      className="flex flex-col gap-4 rounded-2xl bg-[#F5F6F3] p-4 @[40rem]:flex-row @[40rem]:items-center @[40rem]:px-6 @[40rem]:py-5"
    >
      <div className="flex min-w-0 flex-1 items-start gap-4 @[40rem]:items-center">
        <Icon name="lifebuoy" size={24} className="text-[#127A26]" />
        <div className="flex min-w-0 flex-col gap-0.5">
          <h2 id={titleId} className="nt-body-l-bold text-[#0D110D]">
            {account.problemTitle}
          </h2>
          {account.problemText ? (
            <p className="nt-body-m text-[#363D36]">{account.problemText}</p>
          ) : null}
        </div>
      </div>
      <div className="flex gap-4">
        <Button
          asChild
          variant="outline"
          size="md"
          iconLeft="arrow-u-up-left"
          className="flex-1 @[40rem]:flex-none"
        >
          <Link href={orderReturnHref(identifier)} prefetch={false}>
            {account.returnGoods}
          </Link>
        </Button>
        {complainHref ? (
          <Button asChild variant="ghost" size="md" className="flex-1 @[40rem]:flex-none">
            <Link href={complainHref} prefetch={false}>
              {account.complain}
            </Link>
          </Button>
        ) : null}
      </div>
    </section>
  );
}

/**
 * Figma „Detail objednávky“ (208:12024): back link, heading, „Stav objednávky“, the four info
 * cards, the products with the totals and the help box (not for a cancelled order – nothing to
 * return). The order comes from the customer's own
 * list (it identifies the customer's order for the repeat endpoint),
 * the addresses, methods and prices from `/orders/info` (DLZ `order-detail.tsx`).
 */
export function OrderDetail({ identifier }: { identifier: string }) {
  const { t } = useTranslations();
  const { account, checkout } = useOrderTexts();
  const orders = useCustomerOrders();
  const order = orders.data?.find(item => orderNumber(item) === identifier);
  const info = useOrderInfo(identifier, !!order);

  let content: ReactNode;
  if (orders.isError || info.isError) {
    content = (
      <OrdersMessage
        icon="warning-circle"
        title={t('text594')}
        alert
        action={
          <Button
            variant="outline"
            onClick={() => Promise.all([orders.refetch(), order ? info.refetch() : null])}
            isLoading={orders.isRefetching || info.isRefetching}
          >
            {t('text595')}
          </Button>
        }
      />
    );
  } else if (orders.isPending || (order && info.isPending)) {
    content = <GenericLoader className="h-80" />;
  } else if (!order || !info.data) {
    content = (
      <OrdersMessage
        icon="package"
        title={t('text404')}
        action={
          <Button asChild variant="outline">
            <Link href={ROUTES.ORDERS()} prefetch={false}>
              {account.backToOrders}
            </Link>
          </Button>
        }
      />
    );
  } else {
    const stage = orderStage(info.data.status);
    content = (
      <>
        <DetailHeader order={order} info={info.data} stage={stage} />
        <OrderTimeline
          info={info.data}
          steps={statusSteps(checkout)}
          tracking={stage === 'cancelled' ? '' : trackingUrl(info.data)}
          trackLabel={account.trackShipment}
        />
        <OrderLoyaltyReward reward={info.data.loyalty_reward} />
        <OrderInfoCards info={info.data} stage={stage} />
        <OrderItemsCard order={order} info={info.data} />
        {stage === 'cancelled' ? null : <HelpBox identifier={identifier} />}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <BackLink />
      {content}
    </div>
  );
}
